<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">App Settings</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage application configuration settings</p>
                                </div>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors"
                                        @click="openCreateModal"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create New
                                </button>
                        </div>
                </header>

                <main class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="py-4 px-6 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
                                        <div class="flex items-center gap-2">
                                                <h2 class="text-base font-semibold text-gray-700 m-0">Settings</h2>
                                                <span class="bg-orange-2 text-orange-7 py-0.5 px-2 rounded-full text-xs font-medium">
                                                        {{ filteredSettings.length }} total
                                                </span>
                                        </div>
                                        <div class="flex items-center gap-4 flex-wrap">
                                                <div class="relative">
                                                        <select
                                                                v-model="selectedAffiliateId"
                                                                class="py-2 px-3 pr-8 border border-gray-300 rounded-md text-sm text-gray-700 bg-white cursor-pointer appearance-none min-w-[150px]"
                                                                @change="fetchSettings"
                                                        >
                                                                <option :value="null">All Affiliates</option>
                                                                <option v-for="affiliate in affiliates" :key="affiliate.id" :value="affiliate.id">
                                                                        {{ affiliate.title }}
                                                                </option>
                                                        </select>
                                                        <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                </div>
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search by key..."
                                                                class="py-2 px-3 pl-9 border border-gray-300 rounded-md text-sm w-[200px]"
                                                        >
                                                </div>
                                                <button
                                                        class="inline-flex items-center gap-1.5 py-2 px-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer transition-colors"
                                                        @click="refreshData"
                                                >
                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        Refresh
                                                </button>
                                        </div>
                                </div>

                                <div v-if="loading" class="flex justify-center items-center py-16">
                                        <div class="text-center">
                                                <div class="w-10 h-10 border-[3px] border-gray-200 border-t-orange-7 rounded-full animate-spin mx-auto"></div>
                                                <p class="mt-4 text-gray-500 text-sm">Loading settings...</p>
                                        </div>
                                </div>

                                <div v-else-if="paginatedSettings.length === 0" class="text-center py-16">
                                        <svg class="w-12 h-12 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <p class="mt-4 text-gray-500 text-sm">No settings found</p>
                                </div>

                                <div v-else class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Key</th>
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Affiliate</th>
                                                                <th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr
                                                                v-for="setting in paginatedSettings"
                                                                :key="setting.id"
                                                                class="border-t border-gray-200 transition-colors hover:bg-gray-50"
                                                        >
                                                                <td class="py-4 px-6">
                                                                        <span class="text-sm font-medium text-gray-900">{{ setting.key }}</span>
                                                                </td>
                                                                <td class="py-4 px-6">
                                                                        <div v-if="editingId === setting.id" class="flex items-center gap-2">
                                                                                <input
                                                                                        v-model="editValue"
                                                                                        type="text"
                                                                                        class="flex-1 py-1.5 px-2 border border-orange-7 rounded text-sm outline-none"
                                                                                        @keyup.enter="saveEdit(setting)"
                                                                                        @keyup.escape="cancelEdit"
                                                                                >
                                                                                <button
                                                                                        class="py-1 px-2 bg-orange-7 text-white border-none rounded text-xs cursor-pointer"
                                                                                        @click="saveEdit(setting)"
                                                                                >Save</button>
                                                                                <button
                                                                                        class="py-1 px-2 bg-gray-200 text-gray-700 border-none rounded text-xs cursor-pointer"
                                                                                        @click="cancelEdit"
                                                                                >Cancel</button>
                                                                        </div>
                                                                        <span
                                                                                v-else
                                                                                class="text-sm text-gray-500"
                                                                        >{{ setting.value !== null && setting.value !== undefined && setting.value !== '' ? setting.value : '(empty)' }}</span>
                                                                </td>
                                                                <td class="py-4 px-6">
                                                                        <span class="text-sm text-gray-500">{{ getAffiliateName(setting.affiliateid) }}</span>
                                                                </td>
                                                                <td class="py-4 px-6 text-right">
                                                                        <div class="flex items-center justify-end gap-1">
                                                                                <button
                                                                                        class="p-1.5 bg-transparent border-none text-orange-7 cursor-pointer rounded transition-colors hover:bg-orange-2"
                                                                                        title="Edit value"
                                                                                        @click="startEdit(setting)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        class="p-1.5 bg-transparent border-none text-red-500 cursor-pointer rounded transition-colors hover:bg-red-50"
                                                                                        title="Delete setting"
                                                                                        @click="confirmDeleteSetting(setting)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} settings
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <select
                                                        v-model="pageSize"
                                                        class="py-1.5 px-2 border border-gray-300 rounded text-sm text-gray-700"
                                                        @change="currentPage = 1"
                                                >
                                                        <option :value="10">10 per page</option>
                                                        <option :value="25">25 per page</option>
                                                        <option :value="50">50 per page</option>
                                                        <option :value="100">100 per page</option>
                                                </select>
                                                <button
                                                        :disabled="currentPage === 1"
                                                        class="py-1.5 px-3 border border-gray-300 rounded text-sm cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage > 1 ? currentPage-- : null"
                                                >Previous</button>
                                                <span class="text-sm text-gray-700">Page {{ currentPage }} of {{ totalPages }}</span>
                                                <button
                                                        :disabled="currentPage === totalPages"
                                                        class="py-1.5 px-3 border border-gray-300 rounded text-sm cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage < totalPages ? currentPage++ : null"
                                                >Next</button>
                                        </div>
                                </div>
                        </div>
                </main>

                <div v-if="showCreateModal" class="fixed inset-0 z-50">
                        <div class="fixed inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="fixed inset-0 overflow-y-auto">
                                <div class="flex min-h-full items-center justify-center p-4">
                                        <div class="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
                                                <h3 class="text-xl font-semibold text-gray-900 m-0">Create New Setting</h3>
                                                <p class="mt-1 text-sm text-gray-500">Add a new configuration setting.</p>
                                                <div class="mt-6">
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Key *</label>
                                                                <input
                                                                        v-model="newSetting.key"
                                                                        type="text"
                                                                        placeholder="Enter setting key"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                >
                                                        </div>
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Value</label>
                                                                <input
                                                                        v-model="newSetting.value"
                                                                        type="text"
                                                                        placeholder="Enter setting value"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                >
                                                        </div>
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Affiliate</label>
                                                                <select
                                                                        v-model="newSetting.affiliateid"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                >
                                                                        <option :value="null">None (Global)</option>
                                                                        <option v-for="affiliate in affiliates" :key="affiliate.id" :value="affiliate.id">
                                                                                {{ affiliate.title }}
                                                                        </option>
                                                                </select>
                                                        </div>
                                                </div>
                                                <div class="mt-6 flex flex-col gap-3">
                                                        <button
                                                                type="button"
                                                                :disabled="!newSetting.key?.trim() || saving"
                                                                class="w-full py-2.5 px-4 text-sm font-medium text-white bg-orange-7 hover:bg-orange-6 border-none rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="createSetting"
                                                        >
                                                                {{ saving ? 'Creating...' : 'Create Setting' }}
                                                        </button>
                                                        <button
                                                                type="button"
                                                                class="w-full py-2.5 px-4 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer transition-colors"
                                                                @click="closeCreateModal"
                                                        >
                                                                Cancel
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import SettingService, { SettingModel } from 'services/SettingService';
import AffiliateService from 'services/AffiliateService';
import { AffiliateModel } from '@sosocio/creator/interfaces';
import { showDeleteConfirmation } from 'utils/swal';
import Swal from 'sweetalert2';
import type { SweetAlertIcon } from 'sweetalert2';

interface NewSetting {
        key: string;
        value: string;
        affiliateid: number | null;
}

const showToast = (message: string, icon: SweetAlertIcon = 'success'): void => {
        Swal.fire({
                toast: true,
                position: 'top-end',
                icon,
                title: message,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                        popup: 'windmill-swal-toast',
                },
        });
};

export default defineComponent({
        name: 'AppSettingsPage',
        setup() {
                const settings = ref<SettingModel[]>([]);
                const affiliates = ref<AffiliateModel[]>([]);
                const loading = ref<boolean>(false);
                const saving = ref<boolean>(false);
                const showCreateModal = ref<boolean>(false);
                const searchQuery = ref<string>('');
                const selectedAffiliateId = ref<number | null>(null);
                const currentPage = ref<number>(1);
                const pageSize = ref<number>(10);
                const editingId = ref<number | null>(null);
                const editValue = ref<string>('');

                const newSetting = ref<NewSetting>({
                        key: '',
                        value: '',
                        affiliateid: null,
                });

                const filteredSettings = computed<SettingModel[]>(() => {
                        let result = settings.value;
                        if (selectedAffiliateId.value) {
                                result = result.filter(s => s.affiliateid === selectedAffiliateId.value);
                        }
                        if (searchQuery.value) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter(s => String(s.key ?? '').toLowerCase().includes(query));
                        }
                        return result;
                });

                const totalRows = computed<number>(() => filteredSettings.value.length);
                const totalPages = computed<number>(() => Math.ceil(totalRows.value / pageSize.value) || 1);

                const paginatedSettings = computed<SettingModel[]>(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredSettings.value.slice(start, start + pageSize.value);
                });

                const startRow = computed<number>(() => {
                        if (totalRows.value === 0) return 0;
                        return (currentPage.value - 1) * pageSize.value + 1;
                });

                const endRow = computed<number>(() => {
                        return Math.min(currentPage.value * pageSize.value, totalRows.value);
                });

                const getAffiliateName = (affiliateid: number | null): string => {
                        if (!affiliateid) return 'Global';
                        const affiliate = affiliates.value.find(a => a.id === affiliateid);
                        return affiliate ? affiliate.title : 'Unknown';
                };

                const fetchSettings = async (): Promise<void> => {
                        loading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                if (selectedAffiliateId.value) {
                                        params.set('affiliateid', selectedAffiliateId.value.toString());
                                }
                                settings.value = await SettingService.getAll(params);
                        } catch (error: any) {
                                showToast(error.message || 'Failed to load settings', 'error');
                        } finally {
                                loading.value = false;
                        }
                };

                const fetchAffiliates = async (): Promise<void> => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                affiliates.value = await AffiliateService.getAll(params);
                        } catch (error) {
                                console.error('Failed to load affiliates:', error);
                        }
                };

                const refreshData = (): void => {
                        fetchSettings();
                };

                const openCreateModal = (): void => {
                        newSetting.value = { key: '', value: '', affiliateid: null };
                        showCreateModal.value = true;
                };

                const closeCreateModal = (): void => {
                        showCreateModal.value = false;
                };

                const createSetting = async (): Promise<void> => {
                        if (!newSetting.value.key?.trim()) return;
                        saving.value = true;
                        try {
                                const created = await SettingService.create({
                                        key: newSetting.value.key.trim(),
                                        value: newSetting.value.value || '',
                                        affiliateid: newSetting.value.affiliateid,
                                });
                                settings.value.unshift(created);
                                closeCreateModal();
                                showToast('Setting created successfully');
                        } catch (error: any) {
                                showToast(error.message || 'Failed to create setting', 'error');
                        } finally {
                                saving.value = false;
                        }
                };

                const startEdit = (setting: SettingModel): void => {
                        editingId.value = setting.id;
                        editValue.value = setting.value || '';
                };

                const cancelEdit = (): void => {
                        editingId.value = null;
                        editValue.value = '';
                };

                const saveEdit = async (setting: SettingModel): Promise<void> => {
                        try {
                                await SettingService.update(setting.id, { value: editValue.value });
                                setting.value = editValue.value;
                                cancelEdit();
                                showToast('Setting updated successfully');
                        } catch (error: any) {
                                showToast(error.message || 'Failed to update setting', 'error');
                        }
                };

                const confirmDeleteSetting = async (setting: SettingModel): Promise<void> => {
                        const result = await showDeleteConfirmation({
                                title: 'Delete Setting',
                                text: `Are you sure you want to delete "${setting.key}"? This action cannot be undone.`
                        });
                        if (result.isConfirmed) {
                                try {
                                        await SettingService.delete(setting.id);
                                        settings.value = settings.value.filter(s => s.id !== setting.id);
                                        showToast('Setting deleted successfully');
                                } catch (error: any) {
                                        showToast(error.message || 'Failed to delete setting', 'error');
                                }
                        }
                };

                onMounted(() => {
                        fetchAffiliates();
                        fetchSettings();
                });

                return {
                        settings,
                        affiliates,
                        loading,
                        saving,
                        showCreateModal,
                        searchQuery,
                        selectedAffiliateId,
                        currentPage,
                        pageSize,
                        editingId,
                        editValue,
                        newSetting,
                        filteredSettings,
                        paginatedSettings,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        getAffiliateName,
                        fetchSettings,
                        refreshData,
                        openCreateModal,
                        closeCreateModal,
                        createSetting,
                        startEdit,
                        cancelEdit,
                        saveEdit,
                        confirmDeleteSetting,
                };
        },
});
</script>

<style scoped>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
.windmill-swal-toast {
        background-color: #4f46e5 !important;
        color: white !important;
}
.windmill-swal-popup {
        border-radius: 0.75rem !important;
}
.windmill-swal-confirm {
        background-color: #9333ea !important;
        border-radius: 0.5rem !important;
}
.swal2-popup .swal2-styled.swal2-confirm {
        background-color: #9333ea !important;
}
.swal2-popup .swal2-styled.swal2-cancel {
        background-color: #e5e7eb !important;
        color: #374151 !important;
}
</style>
